// Product-form asset uploads: each dropped .blend is uploaded straight to S3
// (multipart with progress and cancel above the threshold in
// components/s3_upload/presign_upload.js), then staged into the creator's
// default library with this product as attach
// intent, then polled until the scan attaches its assets. Reuses the library
// page's upload components; the status list here is deliberately slim.
import * as FilePond from "filepond"
import {
  presignAndUpload,
  abortError,
} from "../../../components/s3_upload/presign_upload"
import { displayFlashMessage, csrfToken } from "../../../util/methods"
import { autoRemoveCompletedUploads } from "../../../components/input_file/auto_remove_completed"
import {
  SPINNER_HTML,
  escapeText,
  safeRefresh,
  createStatusPoller,
  stillScanningNotice,
} from "../../../components/live_status"

var pond = null
var trackedIds = []

var BLEND_MAX_BYTES = 4 * 1024 * 1024 * 1024
var TERMINAL_STATUSES = [
  "processed",
  "rejected",
  "awaiting_confirmation",
  "awaiting_support",
]

$(document).on("turbolinks:load", function () {
  var zone = document.querySelector(".js-product-asset-upload")
  if (!zone) return

  trackedIds = []

  pond = FilePond.create(zone.querySelector(".js-fileInputProductAsset"), {
    allowMultiple: true,
    allowRevert: false,
    allowPaste: false,
    // Spelled in MB because FilePond's size parser doesn't understand "GB".
    maxFileSize: "4096MB",
    // Browsers report no MIME type for .blend — classify by extension.
    acceptedFileTypes: [".blend"],
    fileValidateTypeDetectType: function (source) {
      return Promise.resolve(
        source.name && source.name.toLowerCase().endsWith(".blend")
          ? ".blend"
          : "unknown"
      )
    },
    labelFileTypeNotAllowed: "Only .blend files can be uploaded here",
    fileValidateTypeLabelExpectedTypes: "Expects a .blend file",
    labelIdle:
      'Drag & drop .blend files or <span class="filepond--label-action">Browse</span>',
    server: { process: processProductAssetUpload },
  })

  autoRemoveCompletedUploads(pond)
})

$(document).on("turbolinks:before-cache", function () {
  if (pond) {
    pond.destroy()
    pond = null
  }
  uploadPoller.stop()
})

function processProductAssetUpload(
  fieldName,
  file,
  metadata,
  load,
  error,
  progress,
  abort
) {
  var zone = document.querySelector(".js-product-asset-upload")

  if (!file.name.toLowerCase().endsWith(".blend")) {
    displayFlashMessage("danger", file.name + " is not a .blend file.")
    error("Not a .blend file")
    return
  }
  if (file.size > BLEND_MAX_BYTES) {
    displayFlashMessage(
      "danger",
      file.name +
        " is larger than 4 GB — upload it to an asset library instead."
    )
    error("File too large")
    return
  }

  var controller = new AbortController()

  presignAndUpload(file, "/s3/params", {
    signal: controller.signal,
    onProgress: function (loaded, total) {
      progress(true, loaded, total)
    },
  })
    .then(function (cachedUpload) {
      // Cancelled after the bytes landed but before staging: do not stage.
      if (controller.signal.aborted) throw abortError()
      return fetch(zone.getAttribute("data-upload-url"), {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ file: cachedUpload }),
      })
    })
    .then(function (response) {
      return response.json().then(function (json) {
        return { ok: response.ok, json: json }
      })
    })
    .then(function (result) {
      if (result.ok) {
        trackedIds.push(result.json.id)
        setStatusLine(
          result.json.id,
          "Scanning " + (result.json.filename || file.name) + "…",
          "text-muted",
          { spinning: true }
        )
        uploadPoller.start()
        load(result.json.id)
      } else {
        var messages =
          (result.json.errors || [])
            .map(function (err) {
              return err.message
            })
            .join(" ") || "Upload failed."
        displayFlashMessage("danger", file.name + ": " + messages)
        error("Rejected")
      }
    })
    .catch(function (err) {
      if (err && err.name === "AbortError") {
        abort()
        return
      }
      displayFlashMessage(
        "danger",
        file.name + " failed to upload — try again."
      )
      error("Upload failed")
    })

  return {
    abort: function () {
      controller.abort()
    },
  }
}

var uploadPoller = createStatusPoller({
  page: function () {
    return document.querySelector(".js-product-asset-upload")
  },
  maxPolls: 100,
  onBudgetExhausted: stillScanningNotice,
  hasPending: function () {
    return trackedIds.length > 0
  },
  url: function () {
    var zone = document.querySelector(".js-product-asset-upload")
    var query = new URLSearchParams()
    trackedIds.forEach(function (id) {
      query.append("ids[]", id)
    })
    return zone.getAttribute("data-statuses-url") + "?" + query.toString()
  },
  onResults: function (entries) {
    var byId = {}
    entries.forEach(function (entry) {
      byId[entry.id] = entry
    })
    // Ids missing from the response were deleted elsewhere (e.g. the upload
    // was canceled from the library page) — stop tracking them instead of
    // treating them as finished or polling for them forever.
    trackedIds = trackedIds.filter(function (id) {
      return byId[id]
    })

    var progressed = false
    var stillScanning = []
    trackedIds.forEach(function (id) {
      renderStatusEntry(byId[id])
      if (TERMINAL_STATUSES.indexOf(byId[id].status) === -1) {
        stillScanning.push(id)
      } else {
        progressed = true
      }
    })
    var finished = trackedIds.length > 0 && stillScanning.length === 0
    trackedIds = stillScanning

    if (finished) {
      // Re-render the attached-grants table with the scan's results —
      // unless the section form has unsaved edits a reload would discard.
      safeRefresh("Assets attached.")
    }
    return progressed
  },
})

function renderStatusEntry(entry) {
  if (entry.status === "processed") {
    var names = (entry.attached_assets || []).join(", ")
    var note = entry.deduplicated ? "already scanned — attached" : "attached"
    setStatusLine(
      entry.id,
      "✓ " + (names || "Assets") + " " + note + ".",
      "text-success"
    )
  } else if (entry.status === "rejected") {
    var reasons = (entry.rejection_reasons || [])
      .map(function (reason) {
        return reason.message
      })
      .join(" ")
    setStatusLine(
      entry.id,
      "✗ " + (reasons || "Upload rejected."),
      "text-danger"
    )
  } else if (entry.status === "awaiting_confirmation") {
    setStatusLine(
      entry.id,
      "Needs confirmation — some assets already exist. Resolve it in your asset library.",
      "text-warning"
    )
  } else if (entry.status === "awaiting_support") {
    setStatusLine(
      entry.id,
      "Saved with a newer Blender than we support yet — it retries automatically.",
      "text-warning"
    )
  }
}

// The house in-progress treatment: SPINNER_HTML fronts live work; text is
// escaped (filenames and scan messages are not markup).
function setStatusLine(id, text, className, options) {
  var list = document.querySelector(".js-product-asset-upload-status")
  if (!list) return

  var line = list.querySelector('[data-upload-id="' + id + '"]')
  if (!line) {
    line = document.createElement("li")
    line.setAttribute("data-upload-id", id)
    list.appendChild(line)
  }
  var spinning = options && options.spinning
  line.innerHTML = (spinning ? SPINNER_HTML : "") + escapeText(text)
  line.className = className
}
